import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../landing/landing.css';
import './home.css';
import Reveal from '../landing/Reveal';
import StickyCta from '../landing/StickyCta';
import { trackCtaClick, trackPlayStoreClick, PLAY_STORE_URL } from '../landing/track';
import VideoPlayer from '../../components/VideoPlayer';
import AnnualAccessModal, { ANNUAL_PRODUCT_ID } from './AnnualAccessModal';

const PAGE_TITLE = 'Drops eBook — eBooks interativos e o app DropsDay';
const PAGE_DESCRIPTION =
  'Conhecimento em pequenas doses: eBooks interativos e o app DropsDay, com a Clara IA, foco, exercícios guiados e NightMode.';

const APP_FEATURES = [
  { icon: '💬', color: 'purple', title: 'Clara IA', text: 'Converse com a Clara e descubra o recurso certo para o seu momento.' },
  { icon: '📚', color: 'blue', title: 'Biblioteca', text: 'Ebooks interativos, artigos semanais, podcasts e Drops do Dia.' },
  { icon: '✍️', color: 'pink', title: 'Exercícios guiados', text: 'Escrita terapêutica, diário emocional e reflexões com a Clara.' },
  { icon: '⏱️', color: 'green', title: 'Foco', text: 'Pomodoro para criar seu momento de concentração.' },
  { icon: '🌙', color: 'amber', title: 'NightMode', text: 'Ritual da Noite e áudios relaxantes para desacelerar.' },
  { icon: '📊', color: 'teal', title: 'Histórico', text: 'Acompanhe seu nível emocional e sua evolução semana a semana.' },
];

const SCREENS = [
  { img: 'img01', label: 'Clara', alt: 'Tela inicial do DropsDay com a Clara perguntando como pode ajudar' },
  { img: 'img06', label: 'Biblioteca', alt: 'Biblioteca do DropsDay com PodCash: Ansiedade, Superação Inabalável e Treinamento Free' },
  { img: 'img02', label: 'Recursos', alt: 'Menu de recursos: Pomodoro, Palavra do Dia, Músicas Relaxantes e Técnicas de Alívio' },
  { img: 'img03', label: 'Exercícios', alt: 'Exercícios guiados: Descarrego Mental, Carta Não Enviada e Diário Emocional Rápido' },
  { img: 'img04', label: 'Histórico', alt: 'Histórico com o nível emocional da semana e a pontuação dos últimos 7 dias' },
  { img: 'img05', label: 'NightMode', alt: 'NightMode com o Ritual da Noite e áudios de chuva, floresta, oceano e piano' },
];

const EBOOKS = [
  {
    id: 'procrastinacao',
    title: 'Vencendo a Procrastinação',
    text: 'Técnicas práticas e hábitos diários para superar a procrastinação com um método de diagnóstico visual. Entenda o seu padrão e transforme-o em ação produtiva.',
    tag: 'Mais lido',
    covers: [
      { lang: 'PT', src: 'https://dropsebook.com/ebook/img/capa_Ebook01.png', alt: 'Capa do eBook Procrastinação em português' },
      { lang: 'EN', src: 'https://dropsebook.com/en/ebook/img/capa_Ebook01_ingles.png', alt: 'Capa do eBook Procrastination em inglês' },
    ],
    links: { pt: 'https://dropsebook.com/ebook/ebook', en: 'https://dropsebook.com/en/ebook/ebook' },
  },
  {
    id: 'resiliencia',
    title: 'Resiliência: Desafios em Oportunidades',
    text: 'Desenvolva uma mentalidade firme sob pressão e transforme pequenas vitórias em grandes conquistas. Uma jornada mais profunda de autoconhecimento e transformação emocional.',
    tag: 'Jornada de 45 páginas',
    covers: [
      { lang: 'PT', src: 'https://dropsebook.com/ebook2/img/Capa.png', alt: 'Capa do eBook Resiliência em português' },
      { lang: 'EN', src: 'https://dropsebook.com/en/ebook2/img/livro_ingles.png', alt: 'Capa do eBook Resilience em inglês' },
    ],
    links: { pt: 'https://dropsebook.com/ebook2/cover', en: 'https://dropsebook.com/en/ebook2/cover' },
  },
];

// Builds the e-book login URL with the stored annual credentials so the
// reader lands already authenticated (the e-book apps read these params).
function buildDirectAccessUrl(ebookUrl, email) {
  const loginUrl = new URL(ebookUrl);
  if (!loginUrl.pathname.endsWith('/login')) {
    if (loginUrl.pathname.endsWith('/ebook')) {
      loginUrl.pathname = loginUrl.pathname.replace(/\/ebook$/, '/login');
    } else if (loginUrl.pathname.endsWith('/cover')) {
      loginUrl.pathname = loginUrl.pathname.replace(/\/cover$/, '/login');
    } else {
      loginUrl.pathname = `${loginUrl.pathname}/login`;
    }
  }
  loginUrl.searchParams.append('email', email);
  loginUrl.searchParams.append('directAccess', 'true');
  loginUrl.searchParams.append('productId', String(ANNUAL_PRODUCT_ID));
  loginUrl.hash = `auth-${Date.now()}`;
  return loginUrl.toString();
}

const PlayStoreButton = ({ source, className = '', children = 'Baixar no Google Play' }) => (
  <a
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`dd-btn dd-btn--primary ${className}`.trim()}
    onClick={() => trackPlayStoreClick(source)}
  >
    <svg className="dd-play-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 2.5v19l10-9.5L4 2.5zm11.4 10.8 2.6 2.5-11.2 6.3 8.6-8.8zm0-2.6L6.8 1.9 18 8.2l-2.6 2.5zM19.4 9l2.6 1.5c.8.5.8 1.6 0 2.1L19.4 15l-2.9-3 2.9-3z" fill="currentColor" />
    </svg>
    {children}
  </a>
);

const HomePage = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('dark-mode'));
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [annualEmail, setAnnualEmail] = useState('');
  const [hasAnnualAccess, setHasAnnualAccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag ? descriptionTag.getAttribute('content') : null;
    if (descriptionTag) descriptionTag.setAttribute('content', PAGE_DESCRIPTION);

    if (window.dataLayer) {
      window.dataLayer.push({ event: 'page_loaded', page_type: 'home', page_name: PAGE_TITLE });
    }

    const storedToken = localStorage.getItem('ebook_annual_token');
    const storedEmail = localStorage.getItem('ebook_annual_email');
    if (storedToken && storedEmail) {
      setHasAnnualAccess(true);
      setAnnualEmail(storedEmail);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionTag && previousDescription !== null) {
        descriptionTag.setAttribute('content', previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  const closeModal = useCallback(() => setShowAccessModal(false), []);

  const handleVerified = useCallback((email) => {
    setAnnualEmail(email);
    setHasAnnualAccess(true);
  }, []);

  const openEbook = (ebookUrl, ebookId, lang) => {
    trackCtaClick(`ebook_${ebookId}_${lang}`, 'home_ebooks');
    const url = hasAnnualAccess ? buildDirectAccessUrl(ebookUrl, annualEmail) : ebookUrl;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className={`dd-landing dd-home ${isDarkMode ? 'dd-home--dark' : ''}`.trim()}>
      <a href="#conteudo-principal" className="dd-skip-link">
        Pular para o conteúdo
      </a>

      <header className="dd-hero" id="topo">
        <div className="dd-container dd-topbar">
          <a href="#topo" className="dd-brand">
            <img src="/imagens/Logo_drops.png" alt="" width="34" height="34" />
            Drops eBook
          </a>

          <nav className="dd-home-nav" aria-label="Seções da página">
            <a href="#app">App DropsDay</a>
            <a href="#ebooks">eBooks</a>
            <a href="#videos">Vídeos</a>
            <Link to="/contact">Contato</Link>
          </nav>

          <div className="dd-topbar-actions">
            <button
              type="button"
              className="dd-theme-btn"
              onClick={() => setIsDarkMode((v) => !v)}
              aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            <PlayStoreButton source="home_topbar" className="dd-topbar-cta">
              Baixar o app
            </PlayStoreButton>
          </div>
        </div>

        <div className="dd-container dd-hero-grid">
          <div className="dd-hero-copy">
            <span className="dd-eyebrow">✨ Novo: o app DropsDay chegou</span>
            <h1>
              Conhecimento em pequenas doses,
              <br />
              <span className="dd-accent-text">agora no seu bolso.</span>
            </h1>
            <p className="dd-hero-sub">
              O Drops eBook virou app. No <strong>DropsDay</strong> você conversa com a Clara IA, lê
              nossos eBooks interativos, foca com Pomodoro e desacelera com o NightMode — tudo em
              um só lugar.
            </p>

            <div className="dd-hero-ctas">
              <PlayStoreButton source="home_hero" />
              <a
                href="#ebooks"
                className="dd-btn dd-btn--secondary"
                onClick={() => trackCtaClick('scroll_to_ebooks', 'home_hero')}
              >
                Ver os eBooks
              </a>
            </div>

            <div className="dd-hero-trust">
              <span>📱 Grátis no Android</span>
              <span>🔒 Seguro e confiável</span>
              <span>🍬 Leve como um drop</span>
            </div>

            {hasAnnualAccess && (
              <p className="dd-home-access-pill">
                ✓ Acesso anual ativo: <strong>{annualEmail}</strong>
              </p>
            )}
          </div>

          <div className="dd-hero-visual">
            <picture>
              <source media="(min-width: 768px)" srcSet="/imagens/opt/hero-1795.webp" type="image/webp" />
              <source srcSet="/imagens/opt/hero-900.webp" type="image/webp" />
              <source media="(min-width: 768px)" srcSet="/imagens/opt/hero-1795.jpg" />
              <img
                src="/imagens/opt/hero-900.jpg"
                alt="Clara, assistente do DropsDay, e o aplicativo aberto mostrando a conversa, a biblioteca, o foco e o NightMode"
                width="900"
                height="439"
                fetchpriority="high"
                decoding="async"
              />
            </picture>
          </div>
        </div>
      </header>

      <main id="conteudo-principal">
        {/* ---------- App DropsDay ---------- */}
        <section className="dd-section dd-section-soft" id="app" aria-labelledby="app-heading">
          <div className="dd-container">
            <Reveal as="div" className="dd-section-head">
              <span className="dd-eyebrow">App DropsDay</span>
              <h2 id="app-heading">Uma Clara. Vários momentos do seu dia.</h2>
              <p>Tudo o que você conhece do Drops eBook, com novos recursos para aprender, focar, refletir e descansar.</p>
            </Reveal>

            <div className="dd-moments-grid dd-home-features">
              {APP_FEATURES.map((feature, index) => (
                <Reveal as="article" className="dd-moment-card" delay={(index % 3) * 90} key={feature.title}>
                  <div className={`dd-moment-icon dd-moment-icon--${feature.color}`} aria-hidden="true">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="dd-home-screens" role="list" aria-label="Telas do aplicativo DropsDay">
            {SCREENS.map((screen) => (
              <figure className="dd-home-screen" role="listitem" key={screen.img}>
                <div className="dd-phone-frame">
                  <picture>
                    <source srcSet={`/imagens/opt/${screen.img}.webp`} type="image/webp" />
                    <img
                      src={`/imagens/opt/${screen.img}.jpg`}
                      alt={screen.alt}
                      width="720"
                      height="1600"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                </div>
                <figcaption>{screen.label}</figcaption>
              </figure>
            ))}
          </div>

          <div className="dd-container dd-home-app-ctas">
            <PlayStoreButton source="home_app_section" />
            <Link
              to="/leads"
              className="dd-btn dd-btn--secondary"
              onClick={() => trackCtaClick('app_details', 'home_app_section')}
            >
              Conhecer o DropsDay em detalhes
            </Link>
          </div>
        </section>

        {/* ---------- eBooks ---------- */}
        <section className="dd-section" id="ebooks" aria-labelledby="ebooks-heading">
          <div className="dd-container">
            <Reveal as="div" className="dd-section-head">
              <span className="dd-eyebrow">eBooks interativos</span>
              <h2 id="ebooks-heading">Leia no navegador, em português ou inglês.</h2>
              <p>Conteúdos visuais e interativos, com o sabor leve e agradável de um drop 🍬</p>
            </Reveal>

            <div className="dd-home-ebooks">
              {EBOOKS.map((ebook, index) => (
                <Reveal as="article" className="dd-home-ebook" delay={index * 120} key={ebook.id}>
                  <div className="dd-home-ebook-covers">
                    {ebook.covers.map((cover) => (
                      <figure key={cover.lang}>
                        <img src={cover.src} alt={cover.alt} loading="lazy" decoding="async" />
                        <figcaption>{cover.lang}</figcaption>
                      </figure>
                    ))}
                  </div>
                  <div className="dd-home-ebook-body">
                    <span className="dd-home-tag">{ebook.tag}</span>
                    <h3>{ebook.title}</h3>
                    <p>{ebook.text}</p>
                    <div className="dd-home-ebook-actions">
                      <button
                        type="button"
                        className="dd-btn dd-btn--primary"
                        onClick={() => openEbook(ebook.links.pt, ebook.id, 'pt')}
                      >
                        Ler em português
                      </button>
                      <button
                        type="button"
                        className="dd-btn dd-btn--secondary"
                        onClick={() => openEbook(ebook.links.en, ebook.id, 'en')}
                      >
                        Read in English
                      </button>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal as="div" className="dd-home-annual">
              {hasAnnualAccess ? (
                <p>
                  <strong>✓ Plano anual verificado</strong> — os eBooks abrem direto com o acesso de{' '}
                  {annualEmail}.
                </p>
              ) : (
                <p>
                  <strong>🔑 Já tem o plano anual?</strong> Verifique seu e-mail para abrir todos os
                  eBooks sem novo login.
                </p>
              )}
              <button type="button" className="dd-btn dd-btn--secondary" onClick={() => setShowAccessModal(true)}>
                {hasAnnualAccess ? 'Trocar e-mail' : 'Verificar acesso anual'}
              </button>
            </Reveal>
          </div>
        </section>

        {/* ---------- Vídeos ---------- */}
        <section className="dd-section dd-section-soft dd-home-videos" id="videos" aria-label="Vídeos">
          <div className="dd-container">
            <VideoPlayer isDarkMode={isDarkMode} />
          </div>
        </section>

        {/* ---------- Mais Drops ---------- */}
        <section className="dd-section" aria-labelledby="more-heading">
          <div className="dd-container">
            <Reveal as="div" className="dd-section-head">
              <h2 id="more-heading">Mais Drops para você.</h2>
            </Reveal>
            <div className="dd-home-more">
              <Reveal as="article" className="dd-home-more-card">
                <div className="dd-moment-icon dd-moment-icon--green" aria-hidden="true">🔍</div>
                <h3>Palavras Cruzadas Bíblicas</h3>
                <p>Um passatempo leve para exercitar a mente e relembrar passagens da Bíblia.</p>
                <Link to="/crossword" className="dd-btn dd-btn--secondary">
                  Jogar agora
                </Link>
              </Reveal>
              <Reveal as="article" className="dd-home-more-card dd-home-more-card--accent" delay={120}>
                <div className="dd-moment-icon dd-moment-icon--purple" aria-hidden="true">💬</div>
                <h3>Sua opinião nos motiva!</h3>
                <p>
                  Conte como foi sua experiência com os eBooks e com o app e ajude a criar os
                  próximos conteúdos. Resposta em até 24h.
                </p>
                <Link to="/contact" className="dd-btn dd-btn--ghost">
                  💌 Enviar feedback
                </Link>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ---------- CTA final ---------- */}
        <section className="dd-nightmode dd-home-final" aria-labelledby="final-heading">
          <div className="dd-container dd-nightmode-grid">
            <Reveal as="div">
              <div className="dd-section-head">
                <h2 id="final-heading">Seu dia. Seu ritmo. Sua Clara.</h2>
                <p>
                  Baixe o DropsDay e leve o Drops eBook com você: conteúdos, foco, reflexão e
                  momentos de pausa a um toque de distância.
                </p>
              </div>
              <div className="dd-home-final-ctas">
                <PlayStoreButton source="home_final_cta" />
              </div>
            </Reveal>
            <Reveal as="div" delay={120}>
              <div className="dd-phone-frame">
                <picture>
                  <source srcSet="/imagens/opt/img01.webp" type="image/webp" />
                  <img
                    src="/imagens/opt/img01.jpg"
                    alt="Tela inicial do DropsDay com a Clara"
                    width="720"
                    height="1600"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="dd-footer">
        <div className="dd-container">
          <p>© {new Date().getFullYear()} Drops eBook · DropsDay. Todos os direitos reservados.</p>
          <div className="dd-footer-links">
            <a href="#ebooks">eBooks</a>
            <Link to="/leads">App DropsDay</Link>
            <Link to="/crossword">Palavras Cruzadas</Link>
            <Link to="/contact">Fale conosco</Link>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackPlayStoreClick('home_footer')}>
              Google Play
            </a>
          </div>
        </div>
      </footer>

      <StickyCta />

      {showAccessModal && (
        <AnnualAccessModal initialEmail={annualEmail} onClose={closeModal} onVerified={handleVerified} />
      )}
    </div>
  );
};

export default HomePage;
